/**
 * NssSubjectCard — HESA NSS 2025 subject-level Q26 for one (uni, subject)
 * pair. Rendered on `/degree/{id}` pages so families see the course's
 * subject-cluster satisfaction alongside the uni-wide number.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function NssSubjectCard({ uniId, subject }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!uniId) return;
    const url = subject
      ? `/methodology/nss-2025/provider?uni_id=${uniId}&subject=${encodeURIComponent(subject)}`
      : `/methodology/nss-2025/provider?uni_id=${uniId}`;
    api.get(url).then((r) => setData(r.data)).catch(() => setData(null));
  }, [uniId, subject]);

  if (!data?.subject_row) return null;
  const s = data.subject_row;

  // Also grab uni-wide for the delta line.
  const q26 = (data.questions || []).find((q) => q.question_code === "Q26");
  const uniPct = q26?.positivity_pct;

  const deltaVsUni = uniPct != null ? Math.round(s.positivity_pct - uniPct) : null;

  return (
    <div
      className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4"
      data-testid="nss-subject-card"
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] uppercase tracking-wider font-mono">
          HESA verified
        </span>
        <span className="label-uppercase text-emerald-900">
          NSS 2025 · {s.cah_name}
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-4 flex-wrap">
        <div>
          <div className="font-chip text-3xl font-bold text-[#002147]">{s.positivity_pct}%</div>
          <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider">
            Q26 · overall satisfaction · n={s.n_responses ? Number(s.n_responses).toLocaleString() : "—"}
          </div>
        </div>
        {uniPct != null && (
          <div>
            <div className="font-mono text-lg text-stone-700">{uniPct}%</div>
            <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider">
              uni-wide baseline
            </div>
          </div>
        )}
        {deltaVsUni != null && (
          <div>
            <div className={"font-mono text-lg font-semibold " + (deltaVsUni >= 0 ? "text-emerald-700" : "text-red-700")}>
              {deltaVsUni >= 0 ? "+" : ""}{deltaVsUni} pts
            </div>
            <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider">
              subject vs uni-wide
            </div>
          </div>
        )}
      </div>
      <p className="mt-2 text-[11px] text-stone-500 leading-snug">
        Source · HESA NSS 2025 · CAH1 subject cluster. Q26: "Overall, I am satisfied with the quality of my course."
      </p>
    </div>
  );
}
